import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Alert, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faLock,
  faGlobe,
  faUserCog,
  faCrown,
  faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getCommunity } from '../../services/communityService';
import { getEventTypeDisplayName } from '../../utils/EventTypesUtil';
import CommunityUsersList from './CommunityUsersList';

function DesktopCommunityView() {
  const { communityId } = useParams();
  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [supportedEventTypes, setSupportedEventTypes] = useState([]);
  const { isAdminOfCommunity, isMemberOfCommunity } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Fetch community data
  useEffect(() => {
    const fetchCommunityData = async () => {
      if (!communityId) return;

      try {
        setLoading(true);
        setError('');

        const communityData = await getCommunity(communityId);
        if (communityData) {
          setCommunity(communityData);

          // Parse supported event types
          if (communityData.supported_event_types) {
            try {
              const eventTypes = JSON.parse(communityData.supported_event_types);
              setSupportedEventTypes(eventTypes);
            } catch (error) {
              setSupportedEventTypes([]);
            }
          } else {
            setSupportedEventTypes([]);
          }
        } else {
          setError('Skupnost ni bila najdena.');
        }
      } catch (err) {
        setError('Napaka pri pridobivanju podatkov o skupnosti.');
      } finally {
        setLoading(false);
      }
    };

    fetchCommunityData();
  }, [communityId]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleManageCommunity = () => {
    navigate(`/manage-community/${communityId}`);
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Nalaganje...</span>
          </Spinner>
          <p className="mt-3">Nalaganje skupnosti...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-secondary" onClick={handleGoBack}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Nazaj
        </Button>
      </Container>
    );
  }

  if (!community) {
    return (
      <Container className="py-5">
        <Alert variant="warning">Skupnost ni bila najdena.</Alert>
        <Button variant="outline-secondary" onClick={handleGoBack}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Nazaj
        </Button>
      </Container>
    );
  }

  const isAdmin = isAdminOfCommunity(parseInt(communityId));
  const isMember = isMemberOfCommunity(parseInt(communityId));

  return (
    <Container className="py-4">
      {/* Back Button */}
      <Button
        variant="link"
        className="mb-3 p-0 text-decoration-none"
        onClick={handleGoBack}
      >
        <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
        Nazaj
      </Button>

      {/* Community Header */}
      <Card className="shadow-sm border-0 rounded-4 mb-4">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="flex-grow-1">
              <h2 className="mb-3">{community.name}</h2>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {community.visibility === 'Private' ? (
                  <Badge bg="secondary">
                    <FontAwesomeIcon icon={faLock} className="me-1" />
                    Zasebna
                  </Badge>
                ) : (
                  <Badge bg="info">
                    <FontAwesomeIcon icon={faGlobe} className="me-1" />
                    Javna
                  </Badge>
                )}
                {isAdmin && (
                  <Badge bg="warning" text="dark">
                    <FontAwesomeIcon icon={faCrown} className="me-1" />
                    Administrator
                  </Badge>
                )}
                {!isAdmin && isMember && (
                  <Badge bg="success">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                    Član
                  </Badge>
                )}
              </div>
              {community.description && (
                <p className="text-muted mb-0">{community.description}</p>
              )}
            </div>
            {isAdmin && (
              <Button
                variant="primary"
                onClick={handleManageCommunity}
              >
                <FontAwesomeIcon icon={faUserCog} className="me-2" />
                Upravljaj
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Community Details */}
      <Row className="g-4 mb-4">
        <Col md={6}>
          <Card className="shadow-sm border-0 rounded-4 h-100">
            <Card.Body>
              <h6 className="text-muted mb-2">Urejanje za člane</h6>
              <div className="mt-2">
                {community.allow_member_edits ? (
                  <Badge bg="success" className="px-3 py-2">Omogočeno</Badge>
                ) : (
                  <Badge bg="secondary" className="px-3 py-2">Onemogočeno</Badge>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="shadow-sm border-0 rounded-4 h-100">
            <Card.Body>
              <h6 className="text-muted mb-2">Vidnost</h6>
              <div className="mt-2">
                {community.visibility === 'Private' ? (
                  <Badge bg="secondary" className="px-3 py-2">
                    <FontAwesomeIcon icon={faLock} className="me-1" />
                    Zasebna
                  </Badge>
                ) : (
                  <Badge bg="info" className="px-3 py-2">
                    <FontAwesomeIcon icon={faGlobe} className="me-1" />
                    Javna
                  </Badge>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Event Types */}
      {supportedEventTypes.length > 0 && (
        <Card className="shadow-sm border-0 rounded-4 mb-4">
          <Card.Body className="p-4">
            <h5 className="mb-3">Podprte vrste dogodkov</h5>
            <div className="d-flex flex-wrap gap-2">
              {supportedEventTypes.map((type, index) => (
                <Badge
                  key={index}
                  bg="light"
                  text="dark"
                  className="px-3 py-2"
                  style={{ fontSize: '0.9rem' }}
                >
                  {getEventTypeDisplayName(type, t)}
                </Badge>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Members List */}
      <Card className="shadow-sm border-0 rounded-4">
        <Card.Body className="p-4">
          <CommunityUsersList communityId={communityId} />
        </Card.Body>
      </Card>
    </Container>
  );
}

export default DesktopCommunityView;
